import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  Settings,
  Menu,
  X,
  UserCog,
  BookOpen,
  GraduationCap,
  ClipboardList,
  LogOut,
  FileText,
  BarChart3,
  FileSpreadsheet,
  ScrollText,
  PencilLine,
  ArrowRightLeft,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen
  ,CalendarDays
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatFullName, formatUserRole } from '@/utils/format';

type SidebarProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

/**
 * Componente Sidebar con navegación principal
 * Incluye menú de navegación y información del usuario
 */
const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapsed }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    General: true,
  });
  const { user, logout } = useAuth();

  const navigationSections = [
    {
      label: 'General',
      items: [
        { name: 'Inicio', href: '/dashboard', icon: Home, color: 'text-blue-600', roles: ['ADMIN', 'TEACHER', 'SECRETARY', 'REGISTRY'] },
        { name: 'Años Escolares', href: '/academic-years', icon: Calendar, color: 'text-green-600', roles: ['ADMIN', 'SECRETARY'] },
      ],
    },
    {
      label: 'Académico',
      items: [
        { name: 'Estudiantes', href: '/students', icon: Users, color: 'text-purple-600', roles: ['ADMIN', 'SECRETARY', 'REGISTRY'] },
        { name: 'Docentes', href: '/teachers', icon: UserCog, color: 'text-orange-600', roles: ['ADMIN'] },
        { name: 'Asignaturas', href: '/subjects', icon: BookOpen, color: 'text-indigo-600', roles: ['ADMIN'] },
        { name: 'Calificaciones', href: '/grades', icon: BarChart3, color: 'text-yellow-600', roles: ['ADMIN'] },
      ],
    },
    {
      label: 'Docencia',
      items: [
        { name: 'Mis Clases', href: '/my-classes', icon: GraduationCap, color: 'text-cyan-600', roles: ['TEACHER'] },
        { name: 'Estudiantes', href: '/teacher-students', icon: Users, color: 'text-purple-600', roles: ['TEACHER'] },
      ],
    },
    {
      label: 'Gestión',
      items: [
        { name: 'Agenda institucional', href: '/institutional-activities', icon: CalendarDays, color: 'text-blue-600', roles: ['ADMIN', 'SUPER_ADMIN', 'COORDINATOR', 'DIRECTOR', 'PSYCHOLOGY', 'REGISTRY'] },
        // Comentario: el editor global del SUPER_ADMIN vive en un módulo separado para no
        // mezclar la navegación normal de calificaciones con la sobrescritura administrativa.
        { name: 'Editar Calificaciones', href: '/superadmin/grade-entry', icon: PencilLine, color: 'text-rose-600', roles: ['SUPER_ADMIN'] },
        { name: 'Promoción manual', href: '/superadmin/student-promotion', icon: ArrowRightLeft, color: 'text-indigo-600', roles: ['SUPER_ADMIN'] },
        { name: 'Boletines', href: '/reports', icon: FileSpreadsheet, color: 'text-emerald-600', roles: ['ADMIN', 'SECRETARY', 'REGISTRY'] },
        { name: 'Registros Anecdóticos', href: '/anecdotal-records', icon: ScrollText, color: 'text-pink-600', roles: ['ADMIN', 'TEACHER', 'COORDINATOR', 'DIRECTOR', 'PSYCHOLOGY'] },
        { name: 'Seguimiento por sección', href: '/follow-up', icon: ClipboardList, color: 'text-teal-600', roles: ['COORDINATOR', 'DIRECTOR', 'PSYCHOLOGY'] },
        { name: 'Seguimiento de Calificaciones', href: '/follow-up-grades', icon: BarChart3, color: 'text-cyan-600', roles: ['COORDINATOR', 'DIRECTOR', 'PSYCHOLOGY'] },
        { name: 'Estado de Promoción', href: '/promotion-status', icon: FileText, color: 'text-lime-700', roles: ['ADMIN', 'REGISTRY', 'COORDINATOR', 'DIRECTOR'] },
        { name: 'Excelencia Académica', href: '/student-ranking', icon: BarChart3, color: 'text-violet-700', roles: ['ADMIN', 'REGISTRY', 'COORDINATOR', 'DIRECTOR', 'PSYCHOLOGY'] },
      ],
    },
    {
      label: 'Sistema',
      items: [
        { name: 'Configuración', href: '/settings', icon: Settings, color: 'text-gray-600', roles: ['ADMIN'] },
      ],
    },
  ].map((section) => ({
    ...section,
    items: section.items.filter(item => {
      // Filtrar por rol del usuario
      if (!user?.role) return false;
      if (item.roles.includes(user.role)) {
        return true;
      }
      // SUPER_ADMIN se trata como ADMIN
      const effectiveRole = user.role === 'SUPER_ADMIN' ? 'ADMIN' : user.role;
      return item.roles.includes(effectiveRole);
    }),
  })).filter((section) => section.items.length > 0);

  React.useEffect(() => {
    setOpenSections((current) => {
      const nextState: Record<string, boolean> = {};
      navigationSections.forEach((section) => {
        nextState[section.label] = section.label === 'General' || Boolean(current[section.label]);
      });
      return nextState;
    });
  }, [user?.role]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <>
      {/* Sidebar para desktop */}
      {!collapsed ? (
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200 shadow-sm">
          {/* Logo */}
            <div className="flex items-center gap-3 flex-shrink-0 px-4 py-2 border-b border-gray-200">
              <div className="flex min-w-0 flex-1 items-center justify-center">
                <img src="/Logo.webp" alt="Logo" className="h-32 object-contain" />
              </div>
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                title="Ocultar sidebar"
                aria-label="Ocultar sidebar"
              >
                <PanelLeftClose className="h-5 w-5" />
              </button>
            </div>

          {/* Navegación */}
            <nav className="flex-1 px-4 py-5 space-y-5">
              {navigationSections.map((section) => (
                <div key={section.label} className="space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSections((current) => ({
                        ...current,
                        [section.label]: !current[section.label],
                      }))
                    }
                    className="group flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-50"
                    aria-expanded={Boolean(openSections[section.label])}
                  >
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-600">
                      {section.label}
                    </p>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm">
                      <ChevronDown
                        className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 group-hover:text-slate-700 ${
                          openSections[section.label] ? 'rotate-180' : ''
                        }`}
                      />
                    </span>
                  </button>

                  {openSections[section.label] && (
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                              `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium transition-all duration-200 ${
                                isActive
                                  ? 'bg-gradient-to-r from-primary-50 to-sky-50 text-primary-800 shadow-sm ring-1 ring-primary-100'
                                  : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                              }`
                            }
                          >
                            <span
                              className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-100 transition-transform group-hover:scale-[1.02]`}
                            >
                              <Icon className={`h-4 w-4 ${item.color}`} />
                            </span>
                            <span className="truncate">{item.name}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </nav>

          {/* Información del usuario */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <div className="rounded-2xl bg-slate-50 px-3 py-3 ring-1 ring-slate-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-sky-100 ring-1 ring-primary-100">
                      <span className="text-sm font-bold text-primary-700">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {formatFullName(user?.firstName || '', user?.lastName || '')}
                    </p>
                    <p className="truncate text-[11px] font-medium text-gray-500">
                      {user?.role ? formatUserRole(user.role) : 'Usuario'}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleLogout}
                    className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-500 shadow-sm transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    title="Cerrar sesión"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Salir</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-16 lg:flex-col">
          <div className="flex h-full flex-col items-center gap-3 border-r border-gray-200 bg-white py-3 shadow-lg">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-primary-600 shadow-md transition-all hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
              title="Mostrar sidebar"
              aria-label="Mostrar sidebar"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>

            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 ring-1 ring-slate-100">
              <img src="/Logo.webp" alt="Logo" className="h-10 w-10 object-contain" />
            </div>
          </div>
        </div>
      )}

      {/* Botón de menú móvil */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 bg-white rounded-lg shadow-lg border border-gray-200"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Sidebar móvil */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              {/* Logo móvil */}
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="flex items-center justify-center w-full">
                  <img src="/Logo.webp" alt="Logo" className="h-36 object-contain" />
                </div>
              </div>

              {/* Navegación móvil */}
              <nav className="mt-5 px-2 space-y-4">
                {navigationSections.map((section) => (
                <div key={section.label} className="space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSections((current) => ({
                        ...current,
                        [section.label]: !current[section.label],
                      }))
                    }
                    className="group flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-50"
                    aria-expanded={Boolean(openSections[section.label])}
                  >
                      <p className="px-0 text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-600">
                        {section.label}
                      </p>
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm">
                        <ChevronDown
                          className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 group-hover:text-slate-700 ${
                            openSections[section.label] ? 'rotate-180' : ''
                          }`}
                        />
                      </span>
                    </button>

                    {openSections[section.label] && (
                      <div className="space-y-1">
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <NavLink
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium transition-all duration-200 ${
                                  isActive
                                    ? 'bg-gradient-to-r from-primary-50 to-sky-50 text-primary-800 shadow-sm ring-1 ring-primary-100'
                                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                                }`
                              }
                            >
                              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-100">
                                <Icon className={`h-4 w-4 ${item.color}`} />
                              </span>
                              <span className="truncate">{item.name}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Información del usuario móvil */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <div className="rounded-2xl bg-slate-50 px-3 py-3 ring-1 ring-slate-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-sky-100 ring-1 ring-primary-100">
                      <span className="text-sm font-bold text-primary-700">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {formatFullName(user?.firstName || '', user?.lastName || '')}
                    </p>
                    <p className="truncate text-[11px] font-medium text-gray-500">
                      {user?.role ? formatUserRole(user.role) : 'Usuario'}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleLogout}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    title="Cerrar sesión"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
